import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  CheckCircle2,
  Circle,
  Edit3,
  Loader2,
  Save,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { documentRequestsApi, type DocumentRequest } from '@/api/document-requests';
import { LinkText } from '@/components/LinkText';
import { MigratedCrudHistory } from '@/components/MigratedCrudHistory';
import {
  DocumentRequestAttachments,
  useDocumentRequestAttachmentHistoryRenderer,
} from './DocumentRequestAttachments';

interface DocDetailSheetProps {
  doc: DocumentRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (doc: DocumentRequest) => void;
}

const STATUS_OPTIONS: Array<{ value: DocumentRequest['status']; label: string }> = [
  { value: 'NEW', label: 'Новая' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'READY_TO_PRINT', label: 'К печати' },
  { value: 'ARCHIVED', label: 'Архив' },
];

const FIELD_LABELS: Record<string, string> = {
  clientId: 'Клиент',
  clientName: 'Клиент',
  task: 'Заявка',
  comment: 'Комментарий',
  copies: 'Копии',
  attach: 'Вложения',
  status: 'Статус',
  completedAt: 'Дата выполнения',
  archivedAt: 'Дата архивации',
  createdAt: 'Создано',
  updatedAt: 'Изменено',
};

function formatDateInput(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function formatDateLabel(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ru-RU');
}

function errorMessage(error: unknown, fallback: string): string {
  const response = error as { response?: { data?: { message?: string | string[] } } };
  const message = response.response?.data?.message;
  if (Array.isArray(message)) return message.join('; ');
  return message || fallback;
}

const HIDDEN_HISTORY_FIELDS = [
  'createdById',
  'updatedById',
  'completedById',
  'archivedById',
  'createdBy',
  'updatedBy',
  'completedBy',
  'archivedBy',
  'client',
  'legacyId',
  'legacyUserlistId',
  'legacyCreatedByName',
  'legacyLastChange',
  'legacyLog',
  'importedAt',
  'isDone',
  'isArchived',
];

export function DocDetailSheet({
  doc,
  open,
  onOpenChange,
  onSaved,
}: DocDetailSheetProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editTask, setEditTask] = useState('');
  const [editComment, setEditComment] = useState('');
  const [editActiveFrom, setEditActiveFrom] = useState('');
  const [editStatus, setEditStatus] = useState<DocumentRequest['status']>('NEW');
  const isDone = doc?.status === 'READY_TO_PRINT' || doc?.status === 'ARCHIVED';
  const isArchived = doc?.status === 'ARCHIVED' || !!doc?.isArchived;
  const canEdit = !!doc && !isArchived;
  const statusText = doc?.status === 'ARCHIVED'
    ? 'Архив'
    : doc?.status === 'READY_TO_PRINT'
      ? 'К печати'
      : doc?.status === 'IN_PROGRESS'
        ? 'В работе'
        : 'Новая';
  const historyQuery = useQuery({
    queryKey: ['document-request-history', doc?.id],
    queryFn: () => documentRequestsApi.getHistory(doc!.id),
    enabled: open && !!doc?.id,
  });
  const attachmentHistory = useDocumentRequestAttachmentHistoryRenderer(doc?.id || '');

  useEffect(() => {
    setIsEditing(false);
    setEditTask(doc?.task ?? '');
    setEditComment(doc?.comment ?? '');
    setEditActiveFrom(formatDateInput(doc?.activeFrom));
    setEditStatus(doc?.status ?? 'NEW');
  }, [doc?.id, open]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!doc) throw new Error('Заявка не выбрана');
      return documentRequestsApi.update(doc.id, {
        task: editTask.trim(),
        comment: editComment.trim() || null,
        activeFrom: editActiveFrom || null,
        status: editStatus,
      });
    },
    onSuccess: (updated) => {
      const nextClientId = updated.clientId ?? doc?.clientId;
      if (nextClientId) {
        queryClient.invalidateQueries({ queryKey: ['document-requests', 'client', nextClientId] });
      }
      queryClient.invalidateQueries({ queryKey: ['document-request-history', updated.id] });
      setEditTask(updated.task ?? '');
      setEditComment(updated.comment ?? '');
      setEditActiveFrom(formatDateInput(updated.activeFrom));
      setEditStatus(updated.status);
      setIsEditing(false);
      onSaved?.(updated);
      toast.success('Заявка сохранена');
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'Ошибка сохранения заявки'));
    },
  });

  const handleSave = () => {
    if (!editTask.trim()) {
      toast.error('Заполните текст заявки');
      return;
    }
    saveMutation.mutate();
  };

  const handleCancel = () => {
    setEditTask(doc?.task ?? '');
    setEditComment(doc?.comment ?? '');
    setEditActiveFrom(formatDateInput(doc?.activeFrom));
    setEditStatus(doc?.status ?? 'NEW');
    setIsEditing(false);
  };

  if (!doc) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {doc.task || 'Заявка'}
            {isArchived && (
              <Badge variant="secondary" className="text-xs">
                Архив
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {doc.clientName} • {new Date(doc.updatedAt).toLocaleDateString('ru-RU')}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Статус:</span>
              <Badge
                variant="outline"
                className={
                  isDone
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : 'bg-yellow-50 text-yellow-700 border-yellow-300'
                }
              >
                {isDone ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {statusText}
                  </>
                ) : (
                  <>
                    <Circle className="w-3 h-3 mr-1" />
                    {statusText}
                  </>
                )}
              </Badge>
            </div>
            {canEdit && !isEditing && (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit3 className="h-4 w-4" />
                Редактировать
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Заявка</label>
                <Textarea
                  aria-label="Заявка"
                  value={editTask}
                  onChange={(event) => setEditTask(event.target.value)}
                  rows={3}
                  className="min-h-[96px]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Комментарий</label>
                <Textarea
                  aria-label="Комментарий"
                  value={editComment}
                  onChange={(event) => setEditComment(event.target.value)}
                  rows={2}
                  className="min-h-[72px]"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Актуально с</label>
                  <Input
                    aria-label="Актуально с"
                    type="date"
                    value={editActiveFrom}
                    onChange={(event) => setEditActiveFrom(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Статус</label>
                  <select
                    aria-label="Статус"
                    value={editStatus}
                    onChange={(event) => setEditStatus(event.target.value as DocumentRequest['status'])}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                >
                  <X className="h-4 w-4" />
                  Отмена
                </Button>
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Сохранить
                </Button>
              </div>
            </div>
          ) : (
            <>
              {doc.activeFrom && (
                <div className="text-sm text-muted-foreground">
                  Актуально с: {formatDateLabel(doc.activeFrom)}
                </div>
              )}
              {doc.comment && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">
                    <LinkText>{doc.comment}</LinkText>
                  </p>
                </div>
              )}
              {(doc.copies || doc.attach) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="border-amber-300 bg-background text-amber-800">
                      Старые данные
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    {doc.copies && (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">Копии</div>
                        <div className="whitespace-pre-wrap break-words">{doc.copies}</div>
                      </div>
                    )}
                    {doc.attach && (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">
                          Старое текстовое вложение
                        </div>
                        <div className="whitespace-pre-wrap break-words">{doc.attach}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Created by */}
          {(doc.createdBy?.name || doc.legacyCreatedByName) && (
            <div className="text-sm text-muted-foreground">
              Создал: {doc.createdBy?.name || doc.legacyCreatedByName}
            </div>
          )}

          <DocumentRequestAttachments requestId={doc.id} />

          <MigratedCrudHistory
            legacyLog={doc.legacyLog}
            auditItems={historyQuery.data?.items || []}
            total={historyQuery.data?.total}
            isLoading={historyQuery.isLoading}
            error={historyQuery.error}
            fieldLabels={FIELD_LABELS}
            hiddenFields={HIDDEN_HISTORY_FIELDS}
            renderAuditEntry={attachmentHistory.renderer}
          />
          {attachmentHistory.preview}
        </div>
      </SheetContent>
    </Sheet>
  );
}
